import type { DataTableColumn } from './data-table.types';

/** UI-only columns. Never written to the spreadsheet unless a screen marks them `exportable: true` and gives them a label — reserved ids stay out. */
const NON_EXPORT_COLUMN_IDS = new Set(['actions', 'select', 'expand', 'selection']);

export function dataTableExportColumns<T>(columns: DataTableColumn<T>[]): DataTableColumn<T>[] {
  return (columns ?? []).filter((column) => {
    if (column.exportable === false) return false;
    if (NON_EXPORT_COLUMN_IDS.has(column.id)) return false;
    return String(column.label ?? '').trim().length > 0;
  });
}

function coerceExportPrimitive(value: unknown): string | number | null {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value ? 'Ano' : 'Ne';
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toLocaleDateString('cs-CZ');
  }
  return null;
}

/** Visible cell text for Excel. Template columns use `format`, then filter/sort value, then a primitive field. Objects (ids, nested rows) are left blank. */
export function dataTableExportCell<T>(column: DataTableColumn<T>, row: T): string | number {
  if (column.format) {
    const formatted = coerceExportPrimitive(column.format(row));
    return formatted ?? '';
  }
  const keyed = coerceExportPrimitive(column.filterValue?.(row) ?? column.sortValue?.(row));
  if (keyed != null) return keyed;
  if (row != null && typeof row === 'object' && column.id in (row as object)) {
    const fromField = coerceExportPrimitive((row as Record<string, unknown>)[column.id]);
    if (fromField != null) return fromField;
  }
  return '';
}

export function buildDataTableExportAoA<T>(
  columns: DataTableColumn<T>[],
  rows: T[],
): (string | number)[][] {
  const exportColumns = dataTableExportColumns(columns);
  return [
    exportColumns.map((column) => column.label),
    ...rows.map((row) => exportColumns.map((column) => dataTableExportCell(column, row))),
  ];
}
